import { getClipPlaybackRate, VIDEO_EDITOR_TRACK_TYPE } from "./constants";

const MEDIA_SYNC_TOLERANCE_SECONDS = 0.35;

const isMediaClip = (clip) =>
  clip?.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO ||
  clip?.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO;

const getClipTimelineDuration = ({ clip }) => {
  const inPoint = Number(clip?.inPoint) || 0;
  const outPoint = Number(clip?.outPoint) || 0;
  return Math.max(outPoint - inPoint, 0) / getClipPlaybackRate({ clip });
};

export const findMediaClipAtCompositionTime = ({ edl, compositionTime = 0 }) => {
  const clips = Object.values(edl?.clips?.byId || {}).filter(isMediaClip);
  return (
    clips.find((clip) => {
      const start = Number(clip.position) || 0;
      const end = start + getClipTimelineDuration({ clip });
      return compositionTime >= start && compositionTime < end;
    }) || null
  );
};

export const getTimelineTimeFromMediaElement = ({
  edl,
  mediaElement,
  compositionTime = 0,
}) => {
  const safeCompositionTime = Number.isFinite(compositionTime)
    ? compositionTime
    : 0;
  const clip = findMediaClipAtCompositionTime({
    edl,
    compositionTime: safeCompositionTime,
  });
  if (!clip) {
    return safeCompositionTime;
  }

  const mediaTime = Number(mediaElement?.currentTime);
  if (!Number.isFinite(mediaTime)) {
    return safeCompositionTime;
  }

  const playbackRate = getClipPlaybackRate({ clip });
  const inPoint = Number(clip.inPoint) || 0;
  const derived =
    (Number(clip.position) || 0) + (mediaTime - inPoint) / playbackRate;
  if (
    !Number.isFinite(derived) ||
    Math.abs(derived - safeCompositionTime) > MEDIA_SYNC_TOLERANCE_SECONDS
  ) {
    return safeCompositionTime;
  }

  return derived;
};

export const getActiveHtml5MediaElement = ({
  root,
  edl,
  compositionTime = 0,
} = {}) => {
  if (!root?.querySelectorAll) {
    return null;
  }

  const clip = findMediaClipAtCompositionTime({ edl, compositionTime });
  if (clip) {
    const layer = root.querySelector(
      `[data-video-editor-video-layer="${clip.id}"]`
    );
    const nested = layer?.querySelector?.("video, audio");
    if (nested) {
      return nested;
    }
  }

  const mediaNodes = [
    ...(root.querySelectorAll("video") || []),
    ...(root.querySelectorAll("audio") || []),
  ];
  return mediaNodes.find((node) => !node.paused && !node.ended) || null;
};

export const resolvePlaybackTimelineTime = ({
  edl,
  root,
  compositionTime = 0,
}) =>
  getTimelineTimeFromMediaElement({
    edl,
    mediaElement: getActiveHtml5MediaElement({
      root,
      edl,
      compositionTime,
    }),
    compositionTime,
  });
